import {
	Avatar,
	CircularProgress,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { orderBy } from 'lodash';

import { ReactComponent as Delete } from 'common/assets/images/delete.svg';
import { ReactComponent as Paper } from 'common/assets/images/paper.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { maxFileSizeAllowedForRegInventoryComment } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { REG_INV_COMMENT_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setCommentCount,
	setIsCommentAdded,
} from 'common/store/reducer/regulationInventoryReducer';
import {
	dateInCorrectFormatWithFullYearTime,
	formatAssigneeName,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import FileDrop from '../../../../../DataIngestion/FileUpload/FileDrop';
import {
	getCommentFileData,
	getRegInventComment,
	getTaskComment,
	saveCommentFileData,
	saveTaskComment,
	updateTaskComment,
} from '../../../api';
import { ITaskComment, ITaskCreateComment } from '../../../api/type-def';

import controlTask from './Tasks.module.scss';

interface ITaskCommentProps {
	taskId: string;
	type: 'standard' | 'normal';
	taskStatus?: any;
}

const { OK, BAD_REQUEST, NO_CONTENT } = HTTP_STATUS_CODES;
const TaskComment = (props: ITaskCommentProps) => {
	const { taskId, type, taskStatus } = props;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;
	const dispatch = useDispatch();
	const [userComment, setUserComment] = useState('');
	const [errors, setErrors] = useState('');
	const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<
		string | undefined
	>(undefined);
	const [fileData, setFileData] = useState<File[]>([]);
	const [errorToast, setErrorToast] = useState(false);
	const [fileWindowFlag, setfileWindowFlag] = useState(false);
	const [availableComments, setAvailableComments] = useState<ITaskComment[]>(
		[],
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	/**
	 * Get the task comments and its associated file
	 * @param taskId task Id
	 */
	const getAvailableComments = async (taskId: string) => {
		try {
			setIsLoading(true);
			let response: any = {
				taskCommentUuid: '',
				comment: '',
				firstName: '',
				fileName: '',
				filePath: '',
				createdBy: '',
				createdDate: '',
				fileData: '',
				email: '',
				status: '',
			};
			if (type === 'normal') {
				response = await getTaskComment(taskId);
			} else if (type === 'standard') {
				response = await getRegInventComment(taskId, taskStatus.split(' ')[0]);
			}
			if (response && response.statusCode === OK) {
				const { responseBody } = response;
				const promises = responseBody.map(async (comment: ITaskComment) => {
					let file;
					let email;
					let firstName;
					if (comment.fileName && comment.taskCommentUuid) {
						const base64File = await getFileData(
							comment.fileName,
							comment.taskCommentUuid,
						);
						file = base64File;
					}
					if (comment.createdBy) {
						firstName = `${comment?.createdByFirstName} ${comment?.createdByLastName}`;
					}
					return {
						...comment,
						fileData: file,
						email,
						firstName,
					};
				});
				const commentData: any = await Promise.all(promises);
				dispatch(setCommentCount(commentData.length));
				setAvailableComments(orderBy(commentData, ['modifiedDate'], ['desc']));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getFileData = async (filename: string, commentId: string) => {
		const response = await getCommentFileData(taskId, commentId, filename);
		if (response && response.statusCode === OK) {
			return response.responseBody;
		}
		return null;
	};

	const hideFileDropHandler = () => {
		setfileWindowFlag(false);
		setFileData([]);
	};

	useEffect(() => {
		getAvailableComments(taskId);
	}, [taskId]);

	const handleFileData = useCallback((data: File[], errorMessage?: string) => {
		setFileData(data);
		setFileUploadErrorMessage(errorMessage);
	}, []);

	const onChange = (event: any) => {
		setUserComment(event.target.value);
	};

	const handleToast = useCallback(() => setErrorToast(false), []);

	const clickFiles = useCallback(() => {
		setfileWindowFlag(!fileWindowFlag);
		setFileData([]);
	}, [fileWindowFlag]);

	const addCommentResponse = async () => {
		let data: ITaskCreateComment = {
			comment: '',
			status: '',
			createdBy: '',
		};
		if (type === 'standard') {
			data = {
				comment: userComment,
				status: taskStatus,
				createdBy: loggedInUser?.userId ?? '',
			};
		} else if (type === 'normal') {
			data = {
				comment: userComment,
				createdBy: loggedInUser?.userId ?? '',
			};
		}
		const response = await saveTaskComment(taskId, data);
		if (
			response &&
			(response.statusCode === OK || response.statusCode === NO_CONTENT) &&
			response.responseBody &&
			response.responseBody[0]
		) {
			const { responseBody } = response;
			return responseBody[0]?.taskCommentUuid || '';
		}
		displayErrorMessage(response);
		return '';
	};
	/**
	 * update comment event
	 */
	const updateCommentResponse = async (fileInfo: any, commentId: string) => {
		let data: ITaskComment = {
			firstName: '',
			fileName: '',
			filePath: '',
			email: '',
			comment: '',
			status: '',
			createdBy: '',
			createdDate: '',
		};
		if (type === 'normal') {
			data = {
				comment: userComment,
				createdBy: loggedInUser?.userId,
				...fileInfo.responseBody,
			};
		} else if (type === 'standard') {
			data = {
				comment: userComment,
				status: taskStatus,
				...fileInfo.responseBody,
			};
		}

		const response = await updateTaskComment(taskId, commentId, data);
		if (
			response &&
			(response.statusCode === OK || response.statusCode === NO_CONTENT)
		) {
			getAvailableComments(taskId);
		}
		displayErrorMessage(response);
	};
	const displayErrorMessage = (response: any) => {
		if (response?.errorBody || response?.error) {
			dispatch(
				setToastMessage({
					message: REG_INV_COMMENT_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			return false;
		}
		return true;
	};
	const addCommentHandler = useCallback(async () => {
		try {
			setIsLoading(true);
			if (userComment.trim().length === 0) {
				setErrors('Please enter a comment before saving.');
				setErrorToast(true);
			} else {
				const commentId = await addCommentResponse();
				if (commentId) {
					let fileInfo: any = {
						success: true,
					};
					if (fileData[0]?.name) {
						fileInfo = await saveFileData(commentId);
						await updateCommentResponse(fileInfo, commentId);
					}
					if (fileInfo?.error || fileInfo?.errorBody) {
						const errorMsg =
							fileInfo.errorBody?.errorMessage || fileInfo?.error.message;
						setErrors(errorMsg);
						setErrorToast(true);
					}
					getAvailableComments(taskId);
					setUserComment('');
					setfileWindowFlag(false);
					setFileData([]);
				}
			}
		} finally {
			setIsLoading(false);
			dispatch(setIsCommentAdded(true));
		}
	}, [fileData, taskId, userComment]);
	/**
	 * Save the File data by calling API
	 */
	const saveFileData = async (commentId: string) => {
		if (fileData[0]) {
			const fd = new FormData();
			fd.append('file', fileData[0]);
			const response = await saveCommentFileData(taskId, commentId, fd);
			if (response && response.statusCode === OK) {
				return { responseBody: response.responseBody, success: true };
			}
			if (
				response &&
				(response.statusCode === BAD_REQUEST || response.isError)
			) {
				const { errorBody, error } = response;
				return { errorBody, error };
			}
		}
		return null;
	};
	/**
	 * Download file
	 * @param fetchData File Data
	 * @param filename filename
	 */
	const imageDownload = useCallback((fetchData: any, filename: string) => {
		const downloadLink = document.createElement('a');
		downloadLink.target = '_blank';
		downloadLink.href = `data:;base64,${fetchData}`;
		downloadLink.download = filename;
		downloadLink.click();
	}, []);

	const dateFormat = (createdDate: string) => {
		if (createdDate) {
			return dateInCorrectFormatWithFullYearTime(
				`${createdDate}${'Z'}`,
			).replace(',', '');
		}
		return '';
	};

	return (
		<Grid
			container
			data-testid="task-comment"
			direction={type === 'standard' ? 'column-reverse' : 'column'}
		>
			{type !== 'standard' && (
				<Grid item className={controlTask['new-comment-title']}>
					Add a new comment
				</Grid>
			)}
			<Grid
				direction={type === 'standard' ? 'column-reverse' : 'column'}
				className={
					type !== 'standard'
						? controlTask['comment-box']
						: controlTask['standard-comment-box']
				}
			>
				<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
					<TextFieldWithLabel
						name="comments"
						id="comments"
						placeholder={
							type === 'standard'
								? 'Please leave a comment (Required)*'
								: 'Write a comment...'
						}
						value={userComment}
						onChange={onChange}
						multiLine
						inputProps={{
							'data-testid': 'task-textfield-comment',
						}}
						showLabel={false}
						error={false}
						helperText=""
						className={controlTask['task-field-width']}
					/>
					<AlertComponent
						open={errorToast}
						toastMessage={errors}
						severity="error"
						handleCloseMessage={handleToast}
						vertical="top"
						horizontal="right"
					/>
				</Grid>
				<Grid item xs={12} p={1}>
					<Stack direction="row">
						<Grid container display="flex" justifyContent="space-between">
							<Grid item>
								<IconButton
									data-testid="task-file-upload"
									onClick={clickFiles}
									disabled={fileData.length > 0}
								>
									<Paper />
								</IconButton>
								Attach file
							</Grid>
							<Grid item>
								<ButtonComp
									btnType="secondary"
									clickHandler={addCommentHandler}
									data-testid="task-add-comment"
									disabled={userComment.length === 0}
									compClass={controlTask['btn-comment']}
								>
									{toSentenceCase('Add Comment')}
								</ButtonComp>
							</Grid>
						</Grid>
					</Stack>
				</Grid>
			</Grid>
			{fileWindowFlag && (
				<Grid container xs={12} p={1} gap="10px">
					<FileDrop
						accept={{
							'text/csv': ['.xls'],
							'application/pdf': ['.pdf'],
							'application/msword': ['.doc', '.docx'],
							'image/jpeg': ['.jpeg', '.jpg'],
							'image/png': ['.png'],
							'image/gif': ['.gif'],
							'application/mspowerpoint': ['.ppt', '.pptx'],
						}}
						handleFileData={handleFileData}
						fileData={fileData}
						isLoading={isLoading}
						hideFileDrop={hideFileDropHandler}
						maxfileSize={maxFileSizeAllowedForRegInventoryComment}
					/>
					{fileUploadErrorMessage && (
						<Grid
							container
							direction="row"
							className={controlTask['file-upload-error-container']}
						>
							<Grid item display="flex" gap="10px">
								<Delete />
								<Grid item xs zeroMinWidth>
									<Typography
										className={controlTask['file-upload-error-header']}
									>
										Alert
									</Typography>
									<Typography
										className={controlTask['file-upload-error-sub-header']}
									>
										{fileUploadErrorMessage}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			)}
			{isLoading ? (
				<CircularProgress size={50} className={controlTask['control-loader']} />
			) : (
				<Grid item xs={12} p={1}>
					{availableComments?.length === 0 && (
						<>{toSentenceCase('No Comment Found')}</>
					)}
					{availableComments?.map((element: ITaskComment) => (
						<Grid
							container
							data-testid={`task-available-${element?.taskCommentUuid}`}
							className={
								type !== 'standard'
									? controlTask['comment-wrapper']
									: controlTask['standard-comment-wrapper']
							}
							key={element?.taskCommentUuid}
						>
							<Stack
								direction="row"
								className={controlTask['full-comment-info']}
							>
								<Avatar className={controlTask['avatar-profile']}>
									{formatAssigneeName(element.firstName)}
								</Avatar>
								<Grid
									container
									className={
										type !== 'standard'
											? controlTask['comment-title']
											: controlTask['standard-comment-title']
									}
								>
									<Grid item pl={1} sm={12} md={12} xl={12} lg={12}>
										<Stack direction="row">
											<Grid container display="flex">
												<Grid
													item
													className={
														type !== 'standard'
															? controlTask['comment-name']
															: controlTask['standard-comment-name']
													}
												>
													{element.firstName}
												</Grid>
												<Grid item>{dateFormat(element.createdDate)}</Grid>
											</Grid>
										</Stack>
									</Grid>
									<Grid
										className={
											type !== 'standard'
												? controlTask['comment-text']
												: controlTask['standard-comment-text']
										}
										item
										pl={1}
										sm={12}
										md={12}
										xl={12}
										lg={12}
									>
										{element.comment}
									</Grid>
									{element.fileName && (
										<Grid
											className={
												type !== 'standard'
													? controlTask['comment-file']
													: controlTask['standard-comment-file']
											}
											item
											sm={12}
											md={12}
											xl={12}
											lg={12}
											pl={1}
											pt={1}
										>
											<Stack direction="row">
												{element.fileName.match(/\.(jpg|jpeg|png|gif|)$/i) && (
													<img
														src={`data:;base64,${element.fileData}`}
														alt={element.fileName}
														width="190"
														height="100"
													/>
												)}
												<ButtonComp
													btnType="text"
													disabled={isExportButtonEnabled}
													compClass={controlTask['download-file']}
													data-testid={`task-available-${element?.taskCommentUuid}-${element.fileName}`}
													clickHandler={() =>
														imageDownload(element.fileData, element.fileName)
													}
												>
													{element.fileName}
												</ButtonComp>
											</Stack>
										</Grid>
									)}
								</Grid>
							</Stack>
						</Grid>
					))}
				</Grid>
			)}
		</Grid>
	);
};
export default TaskComment;
